/**
 * wayai set-connection-credential — set a connection's credential directly
 * (connection-specific), for preview OR production hub connections.
 *
 * Two modes (exactly one), mirroring the UI's "Org Credential / Direct Input":
 *   --org-credential <name>   link an organization credential by display name
 *   --field <field> --stdin   set a raw secret directly (read from stdin)
 *
 * The secret is NEVER passed as an argument or written to YAML — direct mode
 * reads it from stdin. Org-credential references stay the recommended default for
 * shared/rotatable secrets; use this for one-off / connection-specific secrets and
 * for production connections whose `sync_credentials_to_production` is off.
 *
 * Usage:
 *   echo "$SECRET" | wayai set-connection-credential --hub <id|name> --connection "OpenAI" --field api_key --stdin
 *   wayai set-connection-credential --hub <id|name> --connection "OpenAI" --org-credential "openai-prod-key"
 */

import { requireAuth } from '../lib/auth.js';
import { ApiClient } from '../lib/api-client.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { resolveActiveHubId, localHubEnvironment } from '../lib/workspace.js';
import { readStdin } from '../lib/utils.js';

const VALID_FIELDS = ['api_key', 'access_token', 'password', 'refresh_token', 'webhook_secret_token', 'username'] as const;

interface ParsedArgs {
  connection: string | undefined;
  orgCredential: string | undefined;
  field: string | undefined;
  stdin: boolean;
}

export function parseArgs(args: string[]): ParsedArgs {
  let connection: string | undefined;
  let orgCredential: string | undefined;
  let field: string | undefined;
  let stdin = false;
  for (let i = 0; i < args.length; i++) {
    if (args[i] === '--connection' && args[i + 1]) connection = args[++i];
    else if (args[i] === '--org-credential' && args[i + 1]) orgCredential = args[++i];
    else if (args[i] === '--field' && args[i + 1]) field = args[++i];
    else if (args[i] === '--stdin') stdin = true;
  }
  return { connection, orgCredential, field, stdin };
}

function usage(msg?: string): never {
  if (msg) console.error(`${msg}\n`);
  console.error('Usage: wayai set-connection-credential --hub <id|name> --connection <name|id> ( --org-credential <name> | --field <field> --stdin )');
  console.error(`  --org-credential <name>   link an organization credential by display name`);
  console.error(`  --field <field> --stdin   set a raw secret from stdin (field: ${VALID_FIELDS.join(', ')})`);
  process.exit(1);
}

export async function setConnectionCredentialCommand(args: string[]): Promise<void> {
  // resolveActiveHubId parses and removes `--hub <value>` from args.
  const hubId = resolveActiveHubId(args);
  const parsed = parseArgs(args);

  if (!parsed.connection) usage('Missing required flag: --connection <name|id>');

  const orgMode = !!parsed.orgCredential;
  const directMode = !!parsed.field;
  if (orgMode === directMode) {
    usage('Provide exactly one of --org-credential or --field.');
  }
  if (directMode && !VALID_FIELDS.includes(parsed.field as typeof VALID_FIELDS[number])) {
    usage(`Invalid --field "${parsed.field}". Valid fields: ${VALID_FIELDS.join(', ')}`);
  }
  if (directMode && !parsed.stdin) {
    usage('--field requires --stdin (the secret is read from stdin, never an argument).');
  }

  const { organization_id: organizationId } = requireRepoConfig();
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  // Resolve the hub's environment to route the write. Prefer the local folder
  // (works for pulled hubs incl. read-only production mirrors); fall back to the
  // server, where a production hub is discoverable as some preview's
  // `production_hub_id` (`GET /api/ci/hubs` lists preview hubs only).
  let environment = localHubEnvironment(hubId);
  if (!environment) {
    const { hubs } = await client.listHubs(organizationId);
    if (hubs.some((h) => h.hub_id === hubId)) environment = 'preview';
    else if (hubs.some((h) => h.production_hub_id === hubId)) environment = 'production';
  }
  if (!environment) {
    usage(`Could not resolve hub "${hubId}". Pull it first (\`wayai pull --hub ${hubId}\`) or check the id.`);
  }
  const isProduction = environment === 'production';

  // Resolve the connection by display name or id.
  const { connections } = await client.listConnections(hubId);
  const conn = connections.find(
    (c) => c.connection_id === parsed.connection || c.connection_display_name === parsed.connection,
  );
  if (!conn || !conn.connection_id) usage(`Connection "${parsed.connection}" not found on hub ${hubId}.`);
  const connectionId = conn!.connection_id as string;

  // Build the credential-source body.
  const body: Record<string, unknown> = { hub_id: hubId };
  if (orgMode) {
    const { data: creds } = await client.listOrgCredentials(organizationId);
    const cred = creds.find((c) => c.credential_display_name === parsed.orgCredential);
    if (!cred) usage(`Organization credential "${parsed.orgCredential}" not found.`);
    body.organization_credential_id = cred!.organization_credential_id;
  } else {
    if (process.stdin.isTTY) {
      usage('--field requires the secret on stdin. Example: echo "$SECRET" | wayai set-connection-credential ... --field api_key --stdin');
    }
    const secret = (await readStdin())?.trim();
    if (!secret) usage('No secret received from stdin.');
    body[parsed.field as string] = secret;
  }

  const target = isProduction ? 'production' : 'preview';
  console.log(`Setting ${orgMode ? `org credential "${parsed.orgCredential}"` : `${parsed.field}`} on ${target} connection "${conn!.connection_display_name}"...`);

  if (isProduction) {
    await client.setProductionConnectionCredential(connectionId, body);
  } else {
    await client.setPreviewConnectionCredential(connectionId, body);
  }

  console.log('Credential set.');
}
